import { apiClient } from './client';

// Response shapes mirror packages/api/src/modules/gtm-signal-ops/. The web app
// keeps its own copy, as gtm.ts, funnel.ts and outbound.ts do.

export type ScoreBand = 'hot' | 'warm' | 'cool' | 'cold';
export type ScoreTrend = 'warming' | 'flat' | 'cooling';
export type IcpFit = 'core' | 'adjacent' | 'out';

export const BAND_LABELS: Record<ScoreBand, string> = {
  hot: 'Hot',
  warm: 'Warm',
  cool: 'Cool',
  cold: 'Cold',
};

export const ICP_FIT_LABELS: Record<IcpFit, string> = {
  core: 'Core ICP',
  adjacent: 'Adjacent',
  out: 'Out of ICP',
};

export interface HotAccountRow {
  companyId: string;
  name: string;
  domain: string | null;
  country: string | null;
  icpFit: string | null;
  deskTier: string | null;
  productToPitch: string | null;
  fitScore: number;
  intentScore: number;
  totalScore: number;
  retentionScore: number;
  expansionScore: number;
  band: string;
  trend: string;
  delta: number;
  signalCount: number;
  lastSignalAt: string | null;
  lastSignalType: string | null;
  topChannel: string | null;
  /** Attio company record id, when linked. */
  attioRecordId?: string | null;
}

export interface HotAccountsResponse {
  scoreDate: string | null;
  rows: HotAccountRow[];
  /** Accounts matching before the cap; older API builds omit it. */
  total?: number;
  limit?: number;
  /** Hot or warm now, and not on any scored day a week or more before. */
  newWarmThisWeek?: number;
}

export interface ScoreInput {
  type: string;
  channel: string;
  weight: number;
  occurredAt: string;
  ageDays: number;
  contribution: number;
}

export interface AccountDossier {
  company: {
    id: string;
    name: string;
    domain: string | null;
    country: string | null;
    icpClass: string | null;
    icpFit: string | null;
    employeeRange: string | null;
    productToPitch: string | null;
    fitScore: number | null;
  };
  current: {
    totalScore: number;
    intentScore: number;
    retentionScore: number;
    expansionScore: number;
    band: string;
    trend: string;
    delta: number;
    rank: number | null;
    inputs: ScoreInput[];
  } | null;
  history: Array<{ scoreDate: string; totalScore: number; signalCount: number }>;
  journey: Array<{
    occurredAt: string;
    type: string;
    channel: string;
    weight: number;
    isProspectAction: boolean;
    payload: Record<string, unknown>;
  }>;
}

export interface ChannelRow {
  channel: string;
  label: string;
  band: string;
  canCarryUtm: boolean;
  measuringSince: string | null;
  generated: number;
  won: number;
  cost: number | null;
  moneyReturn: number | null;
  /** Bookings this channel touched; a booking touched by three channels counts on all three. */
  bookings: number;
  /** Fractional booking credit (the channel's share of each booking, same split as €). Absent on older builds. */
  bookingCredit?: number;
  /** Of `bookings`, how many are valued from a real Attio deal. Absent on older builds. */
  dealValued?: number;
  touches: number;
  /** Identity confidence × share of bookings at a real deal value, 0–100. */
  confidence: number;
  /** The identity half of `confidence`, 0–100. Absent on older builds. */
  identityConfidence?: number;
  accuracy: number | null;
  trendPct: number | null;
  /** Generated pipeline in the previous period of equal length. */
  priorGenerated?: number;
}

export interface ChannelsPage {
  periodDays: number;
  bands: Array<{
    band: string;
    label: string;
    generated: number;
    won: number;
    cost: number;
    bookings: number;
    rows: ChannelRow[];
  }>;
  /** `bookings` is booking credit (matched bookings no channel touched), the same basis as each row's `bookingCredit`. */
  unattributed: { generated: number; won: number; bookings: number };
  /**
   * Every demo in the period by the Sales › Demo bookings definition, bridged to
   * the ones attribution placed on a company: sales = matched + noCompany +
   * cancelled + other. Absent on older API builds.
   */
  demos?: { sales: number; matched: number; noCompany: number; cancelled: number; other: number };
  /** `bookings` = every demo (demos.sales); € are over the matched bookings only. */
  totals: { generated: number; won: number; cost: number; bookings: number };
  /** How Generated was valued: real Attio deal values where linked, the default estimate otherwise. */
  valuation?: {
    defaultBookingValue: number;
    bookings: number;
    withDealValue: number;
    repeatWindowDays: number;
    /** Non-demo Calendly meetings in the period, not counted as bookings. Absent on older API builds. */
    nonDemoExcluded?: number;
  };
}

export interface HealthReport {
  ledger: {
    total: number;
    last30d: number;
    prospectActions30d: number;
    /** Customer-side actions (platform logins, usage), kept apart from prospect actions. */
    customerActions30d?: number;
    newestAt: string | null;
    /** The channel a touch came through, with the signal types behind it (`types` absent on older builds). */
    byChannel: Array<{ channel: string; count: number; types?: Array<{ type: string; count: number }> }>;
    /** The rest of last30d: rows that are nobody's action (our sends, enrichment), by type. Absent on older builds. */
    otherSignals30d?: { count: number; types: Array<{ type: string; count: number }> };
  };
  coverage: { canonicalCompanies: number; withSignals: number; withScores: number; withAttribution: number };
  dnc: { total: number; byReason: Array<{ reason: string; count: number }> };
  pendingMatchReviews: number;
  ledgerAgeHours: number | null;
  stale: boolean;
}

export interface MatchReviewRow {
  id: string;
  canonicalType: string;
  source: string;
  sourceId: string;
  candidateLabel: string;
  proposedCanonicalId: string | null;
  proposedLabel: string | null;
  /** Domain of the proposed canonical record, when it has one. */
  proposedDomain?: string | null;
  /** Attio record id of the proposed canonical company, when it has one. */
  proposedAttioRecordId?: string | null;
  /** The unmatched row's own domain in the source (a refused webmail domain is not in the label). Newer API builds only. */
  sourceDomain?: string | null;
  /** Attio record id of the unmatched row, when it came from Attio. Newer API builds only. */
  sourceAttioRecordId?: string | null;
  score: number;
  features: Record<string, unknown> | null;
  status: string;
  createdAt: string;
}

export interface WeightContrastRow {
  signalType: string;
  channel: string;
  currentWeight: number;
  presentInConverted: number;
  presentInNonConverted: number;
  lift: number;
  suggestedWeight: number;
}

export interface PipelineResult {
  identity: { scanned: number; linkedDeterministic: number; linkedScored: number; created: number; queuedForReview: number };
  people: { scanned: number; linked: number; created: number };
  firmographics: { updated: number; unchanged: number; domainConflicts: number };
  fit: { updated: number };
  probabilities: { written: number; baseRate: number };
  dnc: { optedOut: number; bounced: number; customers: number; liveDeals: number; inConversation: number; total: number };
  signals: {
    perEmitter: Record<string, { scanned: number; emitted: number; unresolved: number; error?: string }>;
    totals: { scanned: number; emitted: number; unresolved: number };
    failed: string[];
  };
  scoring: {
    scored: number;
    hot: number;
    warm: number;
    cool: number;
    cold: number;
    scoreDate: string;
    tunedWeightsUsed: number;
    halfLifeDays?: number;
    /** Accounts raised to Warm by the fresh-booking floor; absent on older builds. */
    freshBookingFloored?: number;
  };
  attribution: {
    bookings: number;
    attributedRows: number;
    bookingsWithNoTouches: number;
    unresolvedBookings: number;
    internalBookingsSkipped: number;
    nonDemoBookingsSkipped?: number;
    markovFallbacks: number;
    repeatBookingsMerged?: number;
    bookingsWithDealValue?: number;
    model: 'weighted' | 'markov';
    modelReason: string;
    /** The table is rebuilt each run: rows removed and written. Absent on older builds. */
    replaced?: { deleted: number; inserted: number };
  };
  closedWon: { wonDeals: number; assigned: number; unassigned: number; rowsUpdated: number };
  /** Plays re-evaluated at the end of the run; null when that stage failed (then in failedStages). Absent on older builds. */
  plays?: { proposed: number; warnings: string[] } | null;
  failedStages: string[];
}

export async function getHotAccounts(params: { limit?: number; band?: string; icpFit?: string } = {}) {
  const search = new URLSearchParams();
  if (params.limit) search.set('limit', String(params.limit));
  if (params.band) search.set('band', params.band);
  if (params.icpFit) search.set('icpFit', params.icpFit);
  const qs = search.toString();
  return apiClient.get<HotAccountsResponse>(`/signal-ops/accounts${qs ? `?${qs}` : ''}`);
}

export async function getAccountDossier(id: string) {
  return apiClient.get<AccountDossier>(`/signal-ops/accounts/${id}`);
}

export async function getChannelsPage(periodDays: number) {
  return apiClient.get<ChannelsPage>(`/signal-ops/channels?periodDays=${periodDays}`);
}

export async function getSignalOpsHealth() {
  return apiClient.get<HealthReport>('/signal-ops/health');
}

export interface MatchReviewQueue {
  rows: MatchReviewRow[];
  /** Pending pairs after junk hosts are hidden. */
  total?: number;
  limit?: number;
  /** Pending pairs left out because there is nothing to decide (not a company, or the Attio record is gone). Absent on older builds. */
  hiddenJunk?: number;
  /** The left-out pairs by reason; `gone_from_attio` only on builds from 13 Oct 2026. */
  hiddenByReason?: Partial<Record<'mail_host' | 'webmail' | 'no_name' | 'own_company' | 'gone_from_attio', number>>;
  /** The tier the rows were filtered to, and the pairs in it before the page cap. */
  tier?: MatchScoreTier;
  tierTotal?: number;
  /** Pairs per score tier across the whole queue: 1.00, 0.71–0.99, 0.70 and below. */
  tierCounts?: Record<Exclude<MatchScoreTier, 'all'>, number>;
  decidedLast7Days?: number;
  /** Company links made without a human ÷ (those + human + pending pairs), 0–100. */
  /** Pending same-registrable-domain pairs, linked by the pipeline and left out of the queue. Absent on an older API. */
  autoLinkedSameDomain?: number;
  autoMatch?: { auto: number; human: number; pending: number; rate: number | null };
}

export async function getMatchReviews() {
  return apiClient.get<MatchReviewQueue>('/signal-ops/matches');
}

/** Score tiers of the review queue: all, 1.00, 0.71–0.99, 0.70 and below. */
export type MatchScoreTier = 'all' | 'top' | 'mid' | 'low';

/** The queue filtered to one score tier on the server, before its 200-row cap. */
export async function getMatchReviewsInTier(tier: MatchScoreTier) {
  return apiClient.get<MatchReviewQueue>(`/signal-ops/matches?tier=${tier}`);
}

export async function decideMatchReview(id: string, decision: 'accepted' | 'rejected') {
  return apiClient.post<{ ok: true }>(`/signal-ops/matches/${id}/decide`, { decision });
}

export async function getWeightContrast() {
  return apiClient.get<{ rows: WeightContrastRow[] }>('/signal-ops/weights/contrast');
}

export async function runSignalOpsPipeline() {
  return apiClient.post<PipelineResult>('/signal-ops/run', {});
}

// ── The model ───────────────────────────────────────────────────────────────

export interface SignalProbabilityRow {
  signalType: string;
  channel: string;
  /** Accounts with the signal before their first demo booking: the model's evidence. */
  accountsWithSignal: number;
  /** Accounts with the signal at any time, as Signal coverage counts them. Absent on older API builds. */
  accountsAnyTime?: number;
  /** Accounts that only show the signal at or after their first booking. Absent on older API builds. */
  accountsOnlyAfterConversion?: number;
  /** The conversion itself or what follows it (demo booked, meeting held). Absent on older API builds. */
  isOutcome?: boolean;
  convertedWithSignal: number;
  observedRate: number;
  baseRate: number;
  posteriorMean: number;
  credibleLow: number;
  credibleHigh: number;
  liftRatio: number;
  logLikelihoodRatio: number;
  derivedWeight: number;
  currentWeight: number;
  trusted: boolean;
}

export interface ProbabilityModel {
  baseRate: number;
  population: number;
  conversions: number;
  rows: SignalProbabilityRow[];
}

export interface RemovalEffectRow {
  channel: string;
  removalEffect: number;
  creditShare: number;
  paths: number;
  convertingPaths: number;
}

export interface RemovalEffectModel {
  paths: number;
  convertingPaths: number;
  baseConversionProbability: number;
  effects: RemovalEffectRow[];
  usable: boolean;
  reason?: string;
}

export type CoverageState = 'live' | 'no_data' | 'no_emitter';

export interface SignalCoverageRow {
  signalType: string;
  label: string;
  channel: string;
  side: string;
  weight: number;
  state: CoverageState;
  ledgerRows: number;
  accounts: number;
  newestAt: string | null;
  note: string;
  /** The one silent-emitter verdict (API): never wrote, or live but quiet for `staleDays`. Absent on older builds. */
  silence?: 'never_wrote' | 'stale' | null;
}

export interface SignalCoverage {
  live: number;
  noData: number;
  noEmitter: number;
  /** Live types quiet for `staleDays` (a subset of `live`). Absent on older builds. */
  stale?: number;
  /** noData + stale: silent emitters, the same number on every page. Absent on older builds. */
  silent?: number;
  staleDays?: number;
  rows: SignalCoverageRow[];
}

export async function getProbabilityModel() {
  return apiClient.get<ProbabilityModel>('/signal-ops/model/probabilities');
}

export async function getRemovalEffects() {
  return apiClient.get<RemovalEffectModel>('/signal-ops/model/removal-effects');
}

export async function getSignalCoverage() {
  return apiClient.get<SignalCoverage>('/signal-ops/coverage');
}

// ── Funnel stages ───────────────────────────────────────────────────────────
// Mirrors packages/api/src/modules/gtm-signal-ops/funnel-rules.ts and funnel-stages.ts.

export const FUNNEL_LADDER = ['target', 'mal', 'mqa', 'sal', 'opportunity', 'customer'] as const;
export const FUNNEL_STATES = ['recycled', 'disqualified', 'off_icp', 'ex_customer'] as const;
export type FunnelStage = (typeof FUNNEL_LADDER)[number] | (typeof FUNNEL_STATES)[number];

export const FUNNEL_STAGE_LABELS: Record<FunnelStage, string> = {
  target: 'Target',
  mal: 'MAL',
  mqa: 'MQA',
  sal: 'SAL',
  opportunity: 'Opportunity',
  customer: 'Customer',
  recycled: 'Recycled',
  disqualified: 'Disqualified',
  off_icp: 'Off-ICP',
  ex_customer: 'Ex-customer',
};

/** What the acronyms stand for (Lea's lifecycle), for tooltips and the first mention. */
export const FUNNEL_STAGE_LONG: Partial<Record<FunnelStage, string>> = {
  mal: 'Marketing Accepted Lead — engaged',
  mqa: 'Marketing Qualified Account',
  sal: 'Sales Accepted Lead',
};

/** TOFU / MOFU / BOFU, as agreed 2026-10-14. */
export const FUNNEL_SECTION_LABELS: Partial<Record<FunnelStage, string>> = {
  target: 'Before the funnel',
  mal: 'TOFU',
  mqa: 'MOFU',
  sal: 'BOFU',
  opportunity: 'BOFU',
  customer: 'Bowtie',
};

export interface FunnelPerson {
  name: string | null;
  title: string | null;
}

export interface FunnelStageRow {
  companyId: string;
  name: string;
  domain: string | null;
  icpFit: string | null;
  attioRecordId: string | null;
  stage: string;
  computedStage: string;
  basis: string;
  stageEnteredAt: string;
  reason: string;
  band: string | null;
  trend: string | null;
  totalScore: number | null;
  overrideStage: string | null;
  overrideReason: string | null;
  overrideBy: string | null;
  overrideExpiresAt: string | null;
  people: FunnelPerson[];
  /** Days past the follow-up target; null when none applies. */
  overdueDays: number | null;
}

export interface FunnelOverview {
  evaluatedAt: string | null;
  includesRebuiltHistory: boolean;
  grid: Array<{ stage: string; total: number; hot: number; warm: number; cool: number; cold: number; unscored: number }>;
  entries: Array<{ stage: string; last7: number; last30: number }>;
  conversion: Array<{ fromStage: string; entered: number; matured: number; advanced: number; medianDaysToAdvance: number | null }>;
  matureDays: number;
  drivers: Array<{ stage: string; channel: string; n: number }>;
  assets: Array<{ asset: string; n: number }>;
  needsAction: { total: number; rows: FunnelStageRow[] };
  unclassifiedEngaged: number;
  lastRun: { kind: string; ranAt: string; warnings: string[] } | null;
  periodDays: number;
  rules: { windowDays: number; holdDays: number; overrideDays: number; salSlaDays: number; mqaSlaDays: number; mqaThreshold: number; halfLifeDays: number };
}

export interface StageHistoryRow {
  fromStage: string | null;
  toStage: string;
  kind: string;
  occurredAt: string;
  reason: string;
  drivingChannel: string | null;
  channels: string[] | null;
  evidence: Array<{ kind: string; label: string; type?: string; channel?: string; occurredAt?: string; asset?: string | null }>;
  manual: boolean;
  decidedBy: string | null;
  backfilled: boolean;
  alertState: string;
}

export interface FunnelAccountsParams {
  stage?: string;
  companyId?: string;
  q?: string;
  band?: string;
  icpFit?: string;
  sort?: 'recent' | 'heat' | 'oldest';
  limit?: number;
}

export async function getFunnelOverview(periodDays = 180) {
  return apiClient.get<FunnelOverview>(`/signal-ops/funnel?periodDays=${periodDays}`);
}

export async function getFunnelAccounts(params: FunnelAccountsParams = {}) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') search.set(k, String(v));
  const qs = search.toString();
  return apiClient.get<{ rows: FunnelStageRow[]; total: number; limit: number }>(`/signal-ops/funnel/accounts${qs ? `?${qs}` : ''}`);
}

export async function getStageHistory(companyId: string) {
  return apiClient.get<{ rows: StageHistoryRow[] }>(`/signal-ops/funnel/accounts/${companyId}/history`);
}

export async function setStageOverride(companyId: string, stage: FunnelStage, reason: string) {
  return apiClient.post<{ ok: true }>(`/signal-ops/funnel/accounts/${companyId}/override`, { stage, reason });
}

export async function clearStageOverride(companyId: string) {
  return apiClient.delete<{ ok: true }>(`/signal-ops/funnel/accounts/${companyId}/override`);
}

// ── Points (scoring points and settings) ────────────────────────────────────
// Mirrors packages/api/src/modules/gtm-signal-ops/points.ts and score-settings.ts.

export interface PointsRow {
  signalType: string;
  label: string;
  channel: string;
  channelLabel: string;
  group: string;
  intentClass: string;
  defaultPoints: number;
  points: number;
  tuned: boolean;
  setBy: string | null;
  setAt: string | null;
  rationale: string | null;
  suggested: number | null;
  suggestedFrom: number | null;
  captured: 'live' | 'no_data' | 'no_emitter' | string;
  ledgerRows: number;
  accounts: number;
  newestAt: string | null;
}

export interface ScoreSettingRow {
  key: string;
  label: string;
  help: string;
  default: number;
  value: number;
  setBy: string | null;
  setAt: string | null;
  rationale: string | null;
}

export async function getPoints() {
  return apiClient.get<{ signals: PointsRow[]; settings: ScoreSettingRow[] }>('/signal-ops/points');
}

export async function setPoints(signalType: string, points: number, rationale: string) {
  return apiClient.post<{ ok: true }>('/signal-ops/points/signal', { signalType, points, rationale });
}

export async function setScoreSetting(key: string, value: number, rationale: string) {
  return apiClient.post<{ ok: true }>('/signal-ops/points/setting', { key, value, rationale });
}
